import * as React from 'react';
import PropTypes from 'prop-types';
import Hammer from 'hammerjs';
import SvgPanZoom from 'svg-pan-zoom';

import SVGService from '../services/SVG';
import POIService from '../services/POI';
import RoutingService from '../services/Routing';
//import APIService from '../services/API';

import Position from '../classes/Position';
import Waypoint from '../classes/Waypoint';

import RoutingPopup from '../components/RoutingPopup';

/**
 * A React component for displaying the provided map file name
 *
 * @class SVGMap
 * @extends {React.Component}
 */
class SVGMap extends React.Component {
    constructor (props) {
        super(props);

        // Set up state
        this.state = {
            customRouting: null
        };

        // Set up ref for injecting SVG
        this.svgParent = React.createRef();

        // Set up class variables
        this.zoomMin = 1;
        this.zoomMax = 10;
        this.loadedMap = null;
        this.panZoom = null;
        this.users = {};

        // Bind callbacks
        this.handleZoom = this.handleZoom.bind(this);
        this.handleDoubletap = this.handleDoubletap.bind(this);
    }

    render () {
        return (
            <React.Fragment>
                {/* TODO: Move this to a component */}
                <div ref={this.svgParent}
                    className="full-width-height"
                />
                {
                    this.state.customRouting
                        ? <RoutingPopup
                            setOrigin={this.props.setOrigin}
                            setDestination={this.props.setDestination}
                            top={this.state.customRouting.top}
                            nearestWaypoint={this.state.customRouting.nearestWaypoint}
                            clear={() => this.setState({customRouting: null})}
                            />
                        : null
                }
            </React.Fragment>
        );
    }

    componentDidMount () {
        this.displayMap(this.props.mapName);
        this.initializeMapInteraction();
        this.initializeCurrentPositionDisplay();
        this.initializeRouteDisplay();
        this.initializeLocatablesDisplay();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        // If showing a new map, make sure it's drawing everything
        if (prevProps.mapName !== this.props.mapName) {
            this.displayMap();
            this.initializeMapInteraction();
            this.initializeCurrentPositionDisplay();
            this.initializeRouteDisplay();
            this.initializeLocatablesDisplay();
        }

        // If the origin changed, make sure the current position updates
        if (prevProps.origin !== this.props.origin) {
            this.initializeCurrentPositionDisplay();
        }

        // If the origin/destination changed, make sure the route updates
        if (prevProps.destination !== this.props.destination ||
            prevProps.origin !== this.props.origin) {
            this.initializeRouteDisplay();
        }

        // If the locatables have changes, update display
        if (prevProps.locatables !== this.props.locatables) {
            this.initializeLocatablesDisplay();
        }
    }

    displayMap () {
        const { mapName, setSelectedPOI } = this.props;

        // Remove previous map, if exists
        while (this.svgParent.current.firstChild) {
            this.svgParent.current.removeChild(this.svgParent.current.firstChild);
        }

        // ?FIXME: Attaching map SVG as a DOM child?

        this.loadedMap = SVGService.getSVGCloneByName(mapName.id);
        if (!this.loadedMap) { return; }
        this.loadedMap.setAttribute('class', 'full-width-height');
        this.svgParent.current.appendChild(
            this.loadedMap
        );

        // Draw SVG viewBox
        SVGService.drawSVGViewBox(this.loadedMap);
        // Draw POIs
        // TODO: Test this with POI data
        const POIs = POIService.GetPOIsForMap(mapName.id);

        if (POIs) {
          
            for (var i = 0; i < POIs.length; i++) {
                const POI = POIs[i];
                // TODO FIX!! Got working to this point! Data is there.
                SVGService.drawLocatableOnSVG(
                    this.loadedMap,
                    POI.centerPosition,
                    "point-of-interest",
                    POI.id,
                    undefined,
                    () => setSelectedPOI(POI),
                );
            }
        }
    }

    initializeCurrentPositionDisplay () {
        const { mapName, origin } = this.props;

        // Check if we have an origin set, draw it if so
        if (origin && origin.map === mapName) {
            SVGService.drawLocatableOnSVG(
                    this.loadedMap,
                    origin.position,
                    "current-position",
                    "current-position-origin",
                );
        }
    }

    initializeLocatablesDisplay () {
        // Loop through locatables, updating SVG with locations
        Object.values(this.props.locatables)
            .forEach(locatable => {
                // TODO: Check that location is on this map
                // if (locationData.location.includes(this.props.mapName)) {
                SVGService.drawLocatableOnSVG(
                    this.loadedMap,
                    new Position(
                        locatable.Position.X,
                        locatable.Position.Y,
                        locatable.Position.Z,
                    ),
                    "located-user",
                    locatable.Id,
                    undefined, // No style overrides
                    undefined, // No onClick event
                    locatable.Id
                );
                // }
            });
    }

    initializeRouteDisplay () {
        const { origin, destination } = this.props;

        // Check if we have a set route start/end for route display
        if ( origin && destination ) {
            const route = RoutingService.GetRoute(origin, destination);
            if (route !== null) {
                SVGService.drawRouteOnSVG(this.loadedMap, route);
            } else {
                // If route is null, display an error and clear the destination choice
                this.props.setError("No route is available from your current location.");
                this.props.setDestination(null);
            }
        }
    }

    /*
     * Performs various actions to set up display and interactivity of the SVG map
    */
    initializeMapInteraction () {
        // Initialize SVG controls
        this.initializePanZoom();

        // Initialize zoom
        this.handleZoom(this.panZoom.getZoom());
    }

    initializePanZoom () {
        // Custom pan-limiting to keep SVG on screen
        // Based on http://ariutta.github.io/svg-pan-zoom/demo/limit-pan.html
        const limitPan = function (oldPan, newPan) {
            var gutterWidth = 100
                , gutterHeight = 100
                // Computed variables
                , sizes = this.getSizes()
                , leftLimit = -((sizes.viewBox.x + sizes.viewBox.width) * sizes.realZoom) + gutterWidth
                , rightLimit = sizes.width - gutterWidth - (sizes.viewBox.x * sizes.realZoom)
                , topLimit = -((sizes.viewBox.y + sizes.viewBox.height) * sizes.realZoom) + gutterHeight
                , bottomLimit = sizes.height - gutterHeight - (sizes.viewBox.y * sizes.realZoom)

            let customPan = {}
            customPan.x = Math.max(leftLimit, Math.min(rightLimit, newPan.x))
            customPan.y = Math.max(topLimit, Math.min(bottomLimit, newPan.y))

            return customPan
        };

        // Options object as defined in the svg-pan-zoom docs: https://github.com/ariutta/svg-pan-zoom/#how-to-use
        const panZoomOptions = {
            minZoom: this.zoomMin,
            maxZoom: this.zoomMax,
            dblClickZoomEnabled: false,
            onZoom: this.handleZoom,
            // Limit panning to SVG viewBox
            beforePan: limitPan,
            customEventsHandler: {
                // Halt all touch events
                haltEventListeners: ['touchstart', 'touchend', 'touchmove', 'touchleave', 'touchcancel'],

                // Init custom events handler
                init: (options) => {
                    var instance = options.instance,
                        initialScale = 1,
                        pannedX = 0,
                        pannedY = 0;

                    // Init Hammer
                    this.hammer = Hammer(options.svgElement);

                    // Enable pinch
                    this.hammer.get('pinch').set({enable: true});

                    // Handle pinch
                    this.hammer.on('pinchstart pinchmove', function(ev){
                        // On pinch start remember initial zoom
                        if (ev.type === 'pinchstart') {
                            initialScale = instance.getZoom();
                            instance.zoom(initialScale * ev.scale);
                        }
                        instance.zoom(initialScale * ev.scale);
                    });

                    // Handle pan
                    this.hammer.on('panstart panmove', function(ev) {
                        // On pan start reset panned variables
                        if (ev.type === 'panstart') {
                            pannedX = 0;
                            pannedY = 0;
                        }
                        // Pan only the difference
                        instance.panBy({x: ev.deltaX - pannedX, y: ev.deltaY - pannedY});
                        pannedX = ev.deltaX;
                        pannedY = ev.deltaY;
                    });

                    // Handle double tap
                    this.hammer.on('doubletap', this.handleDoubletap);
                },

                // Destroy custom events handler
                destroy: function() {
                    this.hammer.destroy();
                }
            }
        };

        // Initialize pan/zoom functionality
        this.panZoom = SvgPanZoom(this.loadedMap, panZoomOptions);
    }

    /**
    * Callback for zoom events
    * Determines the level of detail for the SVG to display at a given zoom level
    *
    * @param {any} zoomLevel
    * @memberof onSVGLoaded
    */
    handleZoom (zoomLevel) {
        const normalizedZoom = this.getNormalizedZoom(zoomLevel);

        const levelOfDetailGroups = Array.prototype.slice.call(SVGService.getSVGLevelsOfDetail(this.loadedMap), 0);

        // Sort LoD elements by ID (assuming lower number is lower detail)
        levelOfDetailGroups.sort(function (a, b) {
            if(a.id < b.id) return -1;
            if(a.id > b.id) return 1;
            return 0;
        });

        // Loop through LoD groups, determining their visibility
        for (var l = 0; l < levelOfDetailGroups.length; l++) {
            const lod = levelOfDetailGroups[l];
            const normalizedLoDTransition = l / levelOfDetailGroups.length;

            // Visibility is relative to the number of groups and current zoom level
            if (normalizedLoDTransition <= normalizedZoom) {
                lod.style.visibility = "visible";
            } else {
                lod.style.visibility = "hidden";
            }
        }
    }

    /**
    * Performs a normalization calculation for the current zoom level,
    *  as compared to the min and max zoom levels
    *
    * @param {any} zoom
    * @returns {number}
    * @memberof onSVGLoaded
    */
    getNormalizedZoom (zoom) {
        // Make sure zoom is at least 1
        zoom = Math.max(zoom, 1);
        return (zoom - this.zoomMin) / (this.zoomMax - this.zoomMin);
    }

    /**
     * Callback for double-tap events
     * Currently set up to set the routing destination as the nearest waypoint to the double-tap
     *
     * @param {any} ev
     * @memberof SVGMap
     */
    handleDoubletap (ev) {
        // Determine SVG coordinate tapped
        // Create point at click position
        let pt = this.loadedMap.createSVGPoint();
        pt.x = ev.center.x;
        pt.y = ev.center.y;
        // Get transform of clicked point to viewport matrix
        const tapPoint =  pt.matrixTransform(SVGService.getSVGViewport(this.loadedMap).getScreenCTM().inverse());

        // Get nearest waypoint to SVG click position
        const nearestWaypoint = RoutingService.GetWaypointNearestPosition(new Position(tapPoint.x, tapPoint.y, 0), this.props.mapName);
        if (nearestWaypoint) {
            // HACK: Testing iFrame communication
            // this.setState({
            //     customRouting: {
            //         nearestWaypoint: nearestWaypoint,
            //         top: ev.center.y,
            //     }
            // });
            window.parent.postMessage(nearestWaypoint.id, "*");
        } else {
            this.props.setError("No waypoint is available near the selected location.");
        }
    }
}

SVGMap.propTypes = {
    mapName: PropTypes.object.isRequired,
    origin: PropTypes.instanceOf(Waypoint),
    destination: PropTypes.instanceOf(Waypoint),
    locatables: PropTypes.object,
    setOrigin: PropTypes.func.isRequired,
    setDestination: PropTypes.func.isRequired,
    setSelectedPOI: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
}

export default SVGMap;



// WEBPACK FOOTER //
// ./src/components/SVGMap.js